import React, { useState } from 'react';
import { Invoice } from '../types';
import { X, CreditCard, Calendar, DollarSign, Wallet, CheckCircle, FileText } from 'lucide-react';

interface PaymentDialogProps {
  invoice: Invoice;
  onClose: () => void;
  onConfirm: (data: { 
    amount: number; 
    paymentDate: string; 
    method: string; 
    notes: string;
    reference: string;
    receiptNumber: string;
    serviceFee?: number;
  }) => Promise<void>;
}

const PaymentDialog: React.FC<PaymentDialogProps> = ({ invoice, onClose, onConfirm }) => {
  const [serviceFee, setServiceFee] = useState(0);
  const [amount, setAmount] = useState(invoice.amount);
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [method, setMethod] = useState('KNET');
  const [notes, setNotes] = useState('');
  const [reference, setReference] = useState('');
  const [receiptNumber, setReceiptNumber] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await onConfirm({ amount, serviceFee, paymentDate, method, notes, reference, receiptNumber });
      onClose();
    } catch (error) {
      console.error('Payment failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4 animate-fadeIn overflow-hidden">
      <div className="bg-white rounded-2xl w-full max-w-md flex flex-col shadow-2xl animate-scaleIn border border-slate-200 overflow-hidden max-h-[96vh]">
        {/* Header - Fixed at top */}
        <div className="bg-slate-900 p-4 sm:p-5 text-white relative shrink-0">
          <button onClick={onClose} className="absolute top-3 right-3 p-2 hover:bg-white/10 rounded-full transition-colors">
            <X size={20} />
          </button>
          <div className="flex items-center gap-3">
            <div className="bg-fly-500 p-2.5 rounded-xl">
              <CreditCard size={20} />
            </div>
            <div>
              <h2 className="text-lg font-bold">سداد فاتورة</h2>
              <p className="text-slate-400 text-[10px]">#{invoice.id} - {invoice.tenantName}</p>
            </div>
          </div>
        </div>

        {/* Scrollable Form Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-5 sm:p-6 space-y-5 custom-scrollbar">
          <div className="space-y-4">
            {/* Amount */}
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-700 block mr-1 flex items-center gap-2">
                <DollarSign size={13} className="text-fly-600" />
                المبلغ المراد سداده
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(Number(e.target.value))}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 pr-10 focus:border-fly-500 focus:ring-4 focus:ring-fly-500/5 transition-all outline-none font-black text-lg text-slate-800"
                  required
                />
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs font-bold">KWD</span>
              </div>
            </div>

            {/* Service Fee */}
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-700 block mr-1 flex items-center gap-2">
                <DollarSign size={13} className="text-red-600" />
                رسوم خدمات إضافية (اختياري)
              </label>
              <div className="relative">
                <input
                  type="number"
                  step="0.001"
                  value={serviceFee}
                  onChange={(e) => setServiceFee(Number(e.target.value))}
                  className="w-full bg-red-50 border border-red-100 rounded-xl p-3 pr-10 focus:border-red-500 focus:ring-4 focus:ring-red-500/5 transition-all outline-none font-black text-lg text-red-800 placeholder-red-300"
                  placeholder="0.000"
                />
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-red-400 text-xs font-bold">KWD</span>
              </div>
              <p className="text-[10px] text-slate-400 mr-1">أضف أي مصاريف إضافية (مثل: إصلاحات بسيطة، رسوم تحويل) وسيتم إضافتها للإيصال.</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {/* Date */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-700 block mr-1 flex items-center gap-2">
                  <Calendar size={13} className="text-fly-600" />
                  تاريخ الدفع
                </label>
                <input
                  type="date"
                  value={paymentDate}
                  onChange={(e) => setPaymentDate(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl p-2.5 outline-none focus:border-fly-500 transition-all text-sm font-medium"
                  required
                />
              </div>

              {/* Method */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-700 block mr-1 flex items-center gap-2">
                  <Wallet size={13} className="text-fly-600" />
                  وسيلة الدفع
                </label>
                <select
                  value={method}
                  onChange={(e) => setMethod(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl p-2.5 outline-none focus:border-fly-500 transition-all text-sm font-medium appearance-none"
                >
                  <option value="KNET">كي نت (KNET)</option>
                  <option value="Cash">كاش (Cash)</option>
                  <option value="Transfer">تحويل بنكي</option>
                  <option value="Cheque">شيك</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {/* Reference */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-700 block mr-1 flex items-center gap-2">
                  <FileText size={13} className="text-fly-600" />
                  المرجع (Ref)
                </label>
                <input
                  type="text"
                  value={reference}
                  onChange={(e) => setReference(e.target.value)}
                  placeholder="601026030793"
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl p-2.5 outline-none focus:border-fly-500 transition-all text-sm font-medium"
                />
              </div>

              {/* Receipt Number */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-700 block mr-1 flex items-center gap-2">
                  <CheckCircle size={13} className="text-fly-600" />
                  رقم الإيصال
                </label>
                <input
                  type="text"
                  value={receiptNumber}
                  onChange={(e) => setReceiptNumber(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl p-2.5 outline-none focus:border-fly-500 transition-all text-sm font-medium"
                />
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-700 block mr-1">ملاحظات</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 min-h-[80px] outline-none focus:border-fly-500 transition-all text-sm placeholder:text-slate-300"
                placeholder="تفاصيل إضافية..."
              />
            </div>
          </div>

          {/* Action Buttons - Sticky at bottom of card */}
          <div className="flex gap-3 pt-4 border-t border-slate-100">
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 bg-slate-900 text-white rounded-xl py-3 font-bold hover:bg-slate-800 transition-all active:scale-95 flex items-center justify-center gap-2 shadow-lg disabled:opacity-50"
            >
              {isSubmitting ? (
                <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
              ) : (
                <><CheckCircle size={18} /> تأكيد السداد</>
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-5 bg-slate-100 text-slate-600 rounded-xl font-bold hover:bg-slate-200 transition-all"
            >
              إلغاء
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PaymentDialog;
